// Studio level packs (contracts/studio.md §5): the engine's schedule per track, draft packs, per-level
// re-generation, publishing and deleting drafts, and "Generate next pack" (a levelpack job). Reads are open to
// every role; mutations need editor. Kept apart from client.ts / studio.ts like the other feature modules.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "./client";
import { keys } from "./queries";
import { PackResponse, PackSource, Track } from "./schemas";
import { studioKeys } from "./studio";
import { StudioJobResult } from "./studioSchemas";

const count = z.number().int().nonnegative();
const enc = encodeURIComponent;

// ---------------------------------------------------------------- schemas

export const GeneratorType = z.looseObject({ type: z.string(), category: z.string(), components: z.array(z.string()), howTo: z.string().optional(), inSchedule: z.boolean() });
export type GeneratorType = z.infer<typeof GeneratorType>;

export const GeneratorTrack = z.looseObject({
  id: Track,
  title: z.string(),
  palette: z.string(),
  /** Types of the track's schedule (every era). */
  types: z.array(z.string()),
  /** Registry types the track may draw (its category; every type for mixed). New plugins appear here by themselves. */
  availableTypes: z.array(GeneratorType),
  nextIndex: count,
  published: count,
  drafts: z.array(count),
  next: z.looseObject({
    index: count,
    first: z.number().int(),
    last: z.number().int(),
    chapter: z.object({ name: z.string(), palette: z.string() }),
    schedule: z.number().int(),
    generator: z.string(),
    typeMix: z.record(z.string(), z.number()),
    intro: z.record(z.string(), z.string()),
    difficulty: z.object({ start: z.number(), end: z.number() }),
  }),
});
export type GeneratorTrack = z.infer<typeof GeneratorTrack>;

export const LevelGenerator = z.object({ packSize: z.number().int(), tracks: z.array(GeneratorTrack) });
export type LevelGenerator = z.infer<typeof LevelGenerator>;

export const PackListItem = z.object({
  track: Track,
  index: count,
  status: z.enum(["published", "draft"]),
  source: PackSource,
  first: z.number().int(),
  last: z.number().int(),
  levelCount: count,
  chapter: z.string(),
  palette: z.string(),
  sha256: z.string(),
  requires: z.array(z.string()),
  generator: z.string().nullable(),
  createdAt: z.string().nullable(),
  publishedAt: z.string().nullable(),
  jobId: z.string().nullable(),
  types: z.record(z.string(), count).nullable(),
  verified: z.object({ checked: count, ok: count }).nullable(),
  stale: z.boolean(),
});
export type PackListItem = z.infer<typeof PackListItem>;
export const PackList = z.object({ items: z.array(PackListItem) });

export const PublishResult = z.object({
  pack: z.object({
    track: Track,
    index: count,
    first: z.number().int(),
    last: z.number().int(),
    chapter: z.string(),
    palette: z.string(),
    sha256: z.string(),
    requires: z.array(z.string()),
    generator: z.string().nullable(),
    publishedAt: z.string(),
    publishedBy: z.string(),
  }),
  manifestEntry: z.object({ index: count, first: z.number().int(), last: z.number().int(), path: z.string(), sha256: z.string(), chapter: z.string(), requires: z.array(z.string()) }),
  /** locale → the sha256 the manifest lists (and the pack's ETag) in that language. */
  hashes: z.record(z.string(), z.string()),
  verified: z.object({ checked: count, ok: count }),
});
export type PublishResult = z.infer<typeof PublishResult>;

export const DeleteResult = z.object({ deleted: z.object({ track: Track, index: count, sha256: z.string() }) });

export const RegenerateResult = z.object({
  track: Track,
  index: count,
  n: z.number().int(),
  attempt: z.number().int(),
  sha256: z.string(),
  level: z.unknown(),
  verified: z.looseObject({ checked: count, ok: count }),
});
export type RegenerateResult = z.infer<typeof RegenerateResult>;

export interface NewLevelPackJob {
  kind: "levelpack";
  track: string;
  /** Consecutive packs (1–10). */
  count: number;
  typeMix?: Record<string, number>;
  difficulty?: { start: number; end: number };
  seed?: string;
}

export const MAX_PACKS_PER_JOB = 10;

// ---------------------------------------------------------------- client

export const levelPacksApi = {
  generator: (signal?: AbortSignal) => request(LevelGenerator, "GET", "/levels/generator", { signal }),
  list: (q: { track?: string; status?: "published" | "draft" }, signal?: AbortSignal) => request(PackList, "GET", "/levels/packs", { query: q, signal }),
  draft: (track: string, index: number, signal?: AbortSignal) =>
    request(PackResponse, "GET", `/levels/packs/${enc(track)}/${index}`, { query: { status: "draft" }, signal }),
  publish: (track: string, index: number) => request(PublishResult, "POST", `/levels/packs/${enc(track)}/${index}/publish`, { body: {} }),
  deleteDraft: (track: string, index: number) => request(DeleteResult, "DELETE", `/levels/packs/${enc(track)}/${index}`),
  regenerate: (track: string, index: number, n: number) =>
    request(RegenerateResult, "POST", `/levels/packs/${enc(track)}/${index}/levels/${n}/regenerate`, { body: {} }),
  /** 201 { job }: the worker builds `count` drafts at the track's next free indexes. */
  createJob: (body: NewLevelPackJob) => request(StudioJobResult, "POST", "/studio/jobs", { body }),
};

export const levelPackKeys = {
  all: ["levelpacks"] as const,
  generator: ["levelpacks", "generator"] as const,
  list: (track: string | null, status: string | null) => ["levelpacks", "list", track, status] as const,
  draft: (track: string, index: number) => ["levelpacks", "draft", track, index] as const,
};

export const useLevelGenerator = (enabled = true) =>
  useQuery({ queryKey: levelPackKeys.generator, queryFn: ({ signal }) => levelPacksApi.generator(signal), enabled, staleTime: 15_000 });

export const usePackList = (track: string | null = null, status: "published" | "draft" | null = null) =>
  useQuery({
    queryKey: levelPackKeys.list(track, status),
    queryFn: ({ signal }) => levelPacksApi.list({ ...(track ? { track } : {}), ...(status ? { status } : {}) }, signal),
  });

export const useDraftPack = (track: string | undefined, index: number | undefined) =>
  useQuery({
    queryKey: levelPackKeys.draft(track ?? "", index ?? -1),
    queryFn: ({ signal }) => levelPacksApi.draft(track!, index!, signal),
    enabled: Boolean(track) && index !== undefined && Number.isInteger(index) && index >= 0,
    staleTime: 0,
  });

/** Everything a level-pack mutation can change: tracks, packs, drafts, Studio jobs, the audit log. */
function useInvalidateLevels() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: levelPackKeys.all }),
      qc.invalidateQueries({ queryKey: keys.tracks }),
      qc.invalidateQueries({ queryKey: ["levels"] }),
      qc.invalidateQueries({ queryKey: studioKeys.all }),
      qc.invalidateQueries({ queryKey: keys.audit }),
    ]);
}

export function useCreateLevelPackJob() {
  const invalidate = useInvalidateLevels();
  return useMutation({ mutationFn: (body: NewLevelPackJob) => levelPacksApi.createJob(body), onSuccess: invalidate });
}

export function usePublishPack() {
  const invalidate = useInvalidateLevels();
  return useMutation({ mutationFn: ({ track, index }: { track: string; index: number }) => levelPacksApi.publish(track, index), onSuccess: invalidate });
}

export function useDeleteDraft() {
  const invalidate = useInvalidateLevels();
  return useMutation({ mutationFn: ({ track, index }: { track: string; index: number }) => levelPacksApi.deleteDraft(track, index), onSuccess: invalidate });
}

export function useRegenerateLevel() {
  const invalidate = useInvalidateLevels();
  return useMutation({
    mutationFn: ({ track, index, n }: { track: string; index: number; n: number }) => levelPacksApi.regenerate(track, index, n),
    onSuccess: invalidate,
  });
}
